import { get } from "lodash";
import { useRef, useState } from "react";
import DocumentApiClient from "./apiClients/documentApiClient";
import ApiClient from "../../../../lib/apiClient";

export const useDocument = (apiClient: ApiClient) => {
  const documentApiClient = useRef<DocumentApiClient>(
    new DocumentApiClient(apiClient)
  );

  const [documentPreviewCache, setDocumentPreviewCache] = useState<
    Record<string, BlobPart>
  >({});

  /** This function is used to cache the document in the cache */
  const cacheDocument = (documentId: string, blobPart: BlobPart) => {
    const record = Object.keys(documentPreviewCache).find(
      (documentId) => documentPreviewCache[documentId] === documentId
    );

    if (!record) {
      documentPreviewCache[documentId] = blobPart;
      setDocumentPreviewCache({ ...documentPreviewCache });
    }
  };

  const cacheDocumentPage = (
    documentId: string,
    pageId: number,
    blobPart: BlobPart
  ) => {
    cacheDocument(`${documentId}@${pageId}`, blobPart);
  };

  /** This function is used to get the document from the cache or from the server */
  const getDocument = (documentId: string): Promise<BlobPart> => {
    const record = get(documentPreviewCache, documentId);
    let getDoc: Promise<BlobPart> | undefined = undefined;
    if (!record) {
      getDoc = documentApiClient.current.downloadDocument(documentId);
    } else {
      getDoc = new Promise((resolve) => {
        resolve(record);
      });
    }

    return getDoc;
  };

  /** This function is used to download the document from the server */
  const downloadDocument = (documentId: string): Promise<BlobPart> => {
    return documentApiClient.current.downloadDocument(documentId);
  };

  const downloadFile = (content: BlobPart, fileName: string) => {
    const url = window.URL.createObjectURL(new Blob([content]));
    const link = document.createElement("a");
    link.href = url;
    link.target = "_top";
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
  };

  /** This function is used to get the blob url from the blob part */
  const getDocumentBlobUrl = (blobPart: BlobPart) => {
    return getBlobUrl(blobPart, "application/pdf");
  };

  const getDocumentPageBlobUrl = (blobPart: BlobPart) => {
    return getBlobUrl(blobPart, "image/png");
  };

  const getBlobUrl = (blobPart: BlobPart, blobType: string) => {
    const blobFile = new File([new Blob([blobPart])], "Preview", {
      type: blobType,
    });

    const blobUrl = URL.createObjectURL(blobFile);
    return blobUrl;
  };

  const getBase64Url = async (base64Data: string) => {
    // Fetch
    const base64Response = await fetch(base64Data);

    // Get Url
    const blob = await base64Response.blob();
    const blobUrl = URL.createObjectURL(blob);
    return blobUrl;
  };

  return {
    cacheDocument,
    cacheDocumentPage,
    getDocument,
    downloadDocument,
    downloadFile,
    getDocumentBlobUrl,
    getDocumentPageBlobUrl,
    getBase64Url,
  };
};
